import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function SubmitVideoForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentTheme } = useContext(ThemeContext);
  
  const [formData, setFormData] = useState({
    edited_video_link: '',
    submission_notes: '',
    status: 'in_progress' // Default status
  });
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');


  // 🎨 GLOBAL COLORS DEFINITION (Based on Dashboard.css)
  const colors = {
    // Dashboard.css Background/Elements
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    // Primary Colors and Gradients used in Dashboard.css
    primary: currentTheme?.primary || '#6366F1', // Indigo/Violet (used for orb-1, badge-role, chart-assigned)
    secondary: currentTheme?.secondary || '#8B5CF6', // Purple/Pink (used for orb-2)
    accent: currentTheme?.accent || '#06B6D4', // Cyan (used for orb-3)
    success: '#10B981', // Green (used for orb-4, completion-rate, chart-completed)
    warning: '#F59E0B', // Orange (used for chart-unassigned)
    danger: '#EF4444', // Red (used for error-alert background)

    // Specific gradients simplified to match the colors above
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blue/Violet (close to primary/secondary)
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
  };

  // Styles matching the consistent design
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    floatingOrb: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.3,
      animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
      width: '300px',
      height: '300px',
      background: colors.primary,
      top: '10%',
      left: '5%',
      animationDelay: '0s'
    },
    orb2: {
      width: '200px',
      height: '200px',
      background: colors.secondary,
      top: '60%',
      right: '10%',
      animationDelay: '2s'
    },
    orb3: {
      width: '250px',
      height: '250px',
      background: colors.accent,
      bottom: '20%',
      left: '15%',
      animationDelay: '4s'
    },
    orb4: {
      width: '180px',
      height: '180px',
      background: colors.success,
      top: '30%',
      right: '20%',
      animationDelay: '6s'
    },
    gridOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: {
      padding: '40px 40px 30px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    greeting: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '15px'
    },
    pageTitle: {
      fontSize: '3rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '8px 16px',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    badgeText: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.9rem'
    },
    badgeRole: {
      background: colors.primary,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '1.2rem',
      margin: 0,
      maxWidth: '600px'
    },
    messageAlert: {
      color: 'white',
      padding: '20px',
      borderRadius: '15px',
      margin: '0 40px 30px',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    alertIcon: {
      fontSize: '1.5rem'
    },
    alertMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      color: 'white'
    },
    alertClose: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      transition: 'background 0.3s ease'
    },
    content: {
      padding: '0 40px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '25px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '25px 30px'
    },
    cardTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      margin: 0
    },
    cardBody: {
      padding: '40px'
    },
    formGroup: {
      marginBottom: '30px'
    },
    formLabel: {
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      marginBottom: '10px',
      display: 'block'
    },
    formInput: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '12px',
      padding: '15px 20px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)'
    },
    // 🎨 UPDATED DROPDOWN STYLES - NO WHITE COLOR
    dropdown: {
      background: 'rgba(255, 255, 255, 0.1)',
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '12px',
      padding: '15px 20px',
      color: 'white',
      fontSize: '1rem',
      width: '100%',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      cursor: 'pointer',
      appearance: 'none',
      // Custom dark dropdown arrow
      backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23${colors.primary.replace('#', '')}' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 20px center',
      backgroundSize: '12px'
    },
    formText: {
      color: colors.textLight,
      fontSize: '0.85rem',
      marginTop: '8px'
    },
    submitBtn: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1.1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      width: '100%'
    },
    cancelBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '15px 30px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1.1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      width: '100%'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 40px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '2rem',
      margin: '0 0 20px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: {
      color: 'rgba(255, 255, 255, 0.8)',
      margin: 0,
      fontSize: '1.2rem'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    backBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none'
    }
  };

  useEffect(() => {
    const taskData = location.state?.task;
    const taskId = searchParams.get('task_id');

    console.log('Location state:', location.state);
    console.log('Task ID from URL:', taskId);

    if (taskData) {
      setTask(taskData);
      setLoading(false);
    } else if (taskId) {
      fetchTaskDetails(taskId);
    } else {
      setMessage('❌ No task specified');
      setLoading(false);
    }
  }, [location.state, searchParams, navigate]);

  const fetchTaskDetails = async (taskId) => {
    try {
      setLoading(true);
      const editorId = sessionStorage.getItem('userId');
      
      if (!editorId) {
        throw new Error('Please login again');
      }

      console.log('Fetching task details for ID:', taskId);
      
      // Use the get_task_details.php endpoint
      const url = `${API_BASE}/get_task_details.php?task_id=${taskId}&editor_id=${editorId}`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url);
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid JSON response from server');
      }

      console.log('Parsed data:', data);

      if (data.success && data.task) {
        setTask(data.task);
        // Set initial status based on current task status
        setFormData(prev => ({
          ...prev,
          status: data.task.status || 'in_progress'
        }));
      } else {
        throw new Error(data.message || 'Failed to load task details');
      }
    } catch (error) {
      console.error('Error fetching task:', error);
      setMessage('❌ ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.edited_video_link.trim()) {
      setMessage('❌ Please provide a video link');
      return;
    }

    // Validate URL format
    try {
      new URL(formData.edited_video_link);
    } catch (error) {
      setMessage('❌ Please enter a valid URL (e.g., https://drive.google.com/...)');
      return;
    }

    try {
      setSubmitting(true);
      setMessage('');

      const editorId = sessionStorage.getItem('userId');
      
      if (!editorId) {
        throw new Error('Please login again');
      }

      if (!task || !task.id) {
        throw new Error('No task selected');
      }

      const submissionData = {
        task_id: task.id,
        editor_id: editorId,
        edited_video_link: formData.edited_video_link.trim(),
        notes: formData.submission_notes.trim(),
        status: formData.status // Include status in submission
      };

      console.log('Submitting data:', submissionData);

      const response = await fetch(`${API_BASE}/submit_edited_video.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      console.log('Response status:', response.status);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed response:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        setMessage('✅ Video submitted successfully! Redirecting...');
        setTimeout(() => {
          navigate('/my-tasks');
        }, 2000);
      } else {
        throw new Error(data.message || 'Failed to submit video');
      }
    } catch (error) {
      console.error('Error submitting video:', error);
      setMessage('❌ ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.loadingState}>
            <div className="loading-spinner" style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Task Details</h3>
            <p style={styles.loadingSubtext}>Please wait while we fetch task information</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!task) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.content}>
            <div style={styles.messageAlert}>
              <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div className="alert-message" style={styles.alertMessage}>
                <strong>Error</strong>
                <span style={{color: 'white'}}>{message || 'Task not found'}</span>
              </div>
            </div>
            <button 
              style={styles.backBtn}
              onClick={() => navigate('/my-tasks')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to My Tasks
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Animated Background */}
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        {/* Header Section */}
        <div className="submit-video-header" style={styles.header}>
          <div className="header-content" style={styles.headerContent}>
            <div className="welcome-section">
              <div className="greeting" style={styles.greeting}>
                <h1 className="page-title" style={styles.pageTitle}>
                  <i className="fas fa-video me-2"></i>
                  Submit Edited Video
                </h1>
                <div className="welcome-badge" style={styles.welcomeBadge}>
                  <span className="badge-text" style={styles.badgeText}>Video Submission</span>
                  <span className="badge-role" style={styles.badgeRole}>Editor</span>
                </div>
              </div>
              <p className="page-subtitle" style={styles.pageSubtitle}>
                Submit your completed video for review and update task status
              </p>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="message-alert" style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : `1px solid ${colors.danger}`,
            color: message.includes('✅') 
              ? colors.success
              : colors.danger
          }}>
            <div className="alert-icon" style={{...styles.alertIcon, 
              color: message.includes('✅') 
                ? colors.success
                : colors.danger
            }}>
              <i className={`fas ${message.includes('✅') ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            </div>
            <div className="alert-message" style={styles.alertMessage}>
              <strong>{message.includes('✅') ? 'Success' : 'Error'}</strong>
              <span style={{color: 'white'}}>{message}</span>
            </div>
            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="submit-video-content" style={styles.content}>
          <div className="form-section">
            <div className="form-card" style={styles.formCard}>
              <div className="card-header" style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  <i className="fas fa-upload me-2"></i>
                  Video Submission Form
                </h3>
              </div>
              <div className="card-body" style={styles.cardBody}>
                {/* Task Information */}
                <div style={{marginBottom: '40px'}}>
                  <h4 style={{color: 'white', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <i className="fas fa-info-circle" style={{color: colors.accent}}></i>
                    Task Information
                  </h4>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px'}}>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        <i className="fas fa-film me-2"></i>
                        Video Name
                      </label>
                      <input
                        type="text"
                        style={styles.formInput}
                        value={task.video_name || 'Unnamed Video'}
                        readOnly
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        <i className="fas fa-user-tie me-2"></i>
                        Client
                      </label>
                      <input
                        type="text"
                        style={styles.formInput}
                        value={task.client_name || 'N/A'}
                        readOnly
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        <i className="fas fa-user me-2"></i>
                        Operator
                      </label>
                      <input
                        type="text"
                        style={styles.formInput}
                        value={task.operator_name || 'N/A'}
                        readOnly
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        <i className="fas fa-calendar me-2"></i>
                        Shoot Date
                      </label>
                      <input
                        type="text"
                        style={styles.formInput}
                        value={task.shoot_date || 'N/A'}
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                {/* Submission Form */}
                <form onSubmit={handleSubmit}>
                  {/* Status Dropdown - USING UPDATED STYLES */}
                  <div style={styles.formGroup}>
                    <label htmlFor="status" style={styles.formLabel}>
                      <i className="fas fa-flag me-2"></i>
                      Update Status <span style={{color: colors.danger}}>*</span>
                    </label>
                    <select
                      id="status"
                      name="status"
                      style={styles.dropdown} 
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                      disabled={submitting}
                    >
                      <option value="in_progress">
                        🔄 In Progress - Currently working on this task
                      </option>
                      <option value="completed">
                        ✅ Completed - Task is finished and ready for review
                      </option>
                    </select>
                    <div style={styles.formText}>
                      Select the current status of your work on this task
                    </div>
                  </div>

                  {/* Edited Video Link */}
                  <div style={styles.formGroup}>
                    <label htmlFor="edited_video_link" style={styles.formLabel}>
                      <i className="fas fa-link me-2"></i>
                      Edited Video Link <span style={{color: colors.danger}}>*</span>
                    </label>
                    <input
                      type="url"
                      id="edited_video_link"
                      name="edited_video_link"
                      style={styles.formInput}
                      placeholder="https://drive.google.com/..."
                      value={formData.edited_video_link}
                      onChange={handleInputChange}
                      required
                      disabled={submitting}
                    />
                    <div style={styles.formText}>
                      Please provide the Google Drive link to your edited video
                    </div>
                  </div>

                  {/* Submission Notes */}
                  <div style={styles.formGroup}>
                    <label htmlFor="submission_notes" style={styles.formLabel}>
                      <i className="fas fa-sticky-note me-2"></i>
                      Submission Notes (optional)
                    </label>
                    <textarea
                      id="submission_notes"
                      name="submission_notes"
                      style={{...styles.formInput, resize: 'vertical', minHeight: '120px'}}
                      placeholder="Anything the reviewer should know about your submission..."
                      value={formData.submission_notes}
                      onChange={handleInputChange}
                      disabled={submitting}
                    />
                    <div style={styles.formText}>
                      Add any notes about changes made, special effects used, or other important information for the reviewer.
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '40px'}}>
                    <button
                      type="button"
                      style={styles.cancelBtn}
                      onClick={() => navigate('/my-tasks')}
                      disabled={submitting}
                    >
                      <i className="fas fa-times me-2"></i>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        ...styles.submitBtn,
                        opacity: (submitting || !formData.edited_video_link.trim()) ? 0.6 : 1,
                        cursor: (submitting || !formData.edited_video_link.trim()) ? 'not-allowed' : 'pointer'
                      }}
                      disabled={submitting || !formData.edited_video_link.trim()}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner" style={styles.spinner}></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Submit Video
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        @keyframes wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          } 40% {
            transform: scale(1);
          }
        }
        
        /* Custom Hover Effects */
        .form-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
        }
        
        /* Form input focus states */
        .form-input:focus {
          outline: none;
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px ${colors.primary}20;
        }
        
        /* Select dropdown styling - REMOVED WHITE BACKGROUND */
        select option {
          background: ${colors.backgroundDark};
          color: white;
          padding: 12px;
        }

        /* Custom dropdown focus */
        select:focus {
          outline: none;
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px ${colors.primary}20;
        }

        /* Button hover effects */
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
        }

        .cancel-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.2) !important;
          transform: translateY(-2px);
        }

        .back-btn:hover {
          background: rgba(255, 255, 255, 0.2) !important;
          transform: translateY(-2px);
        }

        /* Remove default dropdown styling */
        select {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
        }

        /* Remove default dropdown arrow in IE */
        select::-ms-expand {
          display: none;
        }
      `}</style>
    </Layout>
  );
}

export default SubmitVideoForm;